import { useEffect } from 'react';

import {
  registerGlobalLockHandler,
  registerGlobalUnlockHandler,
} from '@fireflink/base-api-core';
import { ffid } from 'pixel-react';
import { useDispatch } from 'react-redux';

import { useLockToast } from './useLockToast';
import { useActiveProject } from '@/hooks/useActiveProject';
import {
  getActiveProjectId,
  isAllProjectsId,
} from '@/lib/utils/currentProject';
import type { RootState } from '@/store';
import {
  markToastFired,
  setSectionLocked,
  unlockAll,
  unlockProject,
  unlockSection,
} from '@/store/globalLock/globalLockSlice';
import { useAppSelector } from '@/store/hooks';

export const ImportLockBridge = () => {
  const dispatch = useDispatch();
  const sections = useAppSelector(
    (state: RootState) =>
      state.globalLock?.sections as Record<
        string,
        {
          projectId: string;
          toastFired: boolean;
          message?: string;
          userName?: string;
          lockType: 'import' | 'revert';
        }
      >,
  );
  const { activeProjectId } = useActiveProject();
  const fireLockToast = useLockToast();

  useEffect(
    () =>
      registerGlobalLockHandler(
        ({ projectId, sectionType, message, userName, lockType }) => {
          // Read the store directly: not every project switch fires
          // CURRENT_PROJECT_CHANGE_EVENT, so activeProjectId can be stale.
          const current = getActiveProjectId();
          if (isAllProjectsId(current) || current !== projectId) {
            return;
          }

          dispatch(
            setSectionLocked({
              sectionType,
              projectId,
              sessionId: ffid(),
              message,
              userName,
              lockType,
            }),
          );
        },
      ),
    [dispatch],
  );

  useEffect(
    () =>
      registerGlobalUnlockHandler(({ projectId, sectionType }) => {
        if (getActiveProjectId() !== projectId) {
          return;
        }

        dispatch(
          sectionType ? unlockSection(sectionType) : unlockProject(projectId),
        );
      }),
    [dispatch],
  );

  useEffect(() => {
    dispatch(unlockAll());
  }, [activeProjectId, dispatch]);

  useEffect(() => {
    Object.entries(sections ?? {}).forEach(([sectionType, lock]) => {
      if (!lock.toastFired) {
        fireLockToast(
          lock.projectId,
          sectionType,
          lock.message,
          lock.userName,
          lock.lockType,
        );
        dispatch(markToastFired(sectionType));
      }
    });
  }, [sections, fireLockToast, dispatch]);

  return null;
};

export default ImportLockBridge;
